import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { 
  ExternalLink, 
  CheckCircle2, 
  ArrowRight,
  Sparkles,
  Shield,
  Clock,
  DollarSign
} from "lucide-react";

const WEALTHSIMPLE_LINK = "https://www.wealthsimple.com/invite/M_FMNQ";

const steps = [
  {
    number: 1,
    title: "Click the link below",
    description: "Sign up for Wealthsimple → Choose TFSA account",
    icon: ExternalLink,
    color: "text-blue-600",
    bgColor: "bg-blue-50"
  },
  {
    number: 2,
    title: "Verify your identity",
    description: "SIN, ID photo, and a quick selfie",
    icon: Shield,
    color: "text-purple-600",
    bgColor: "bg-purple-50"
  },
  {
    number: 3,
    title: "Connect your bank",
    description: "Connect your bank to Wealthsimple",
    icon: CheckCircle2,
    color: "text-emerald-600",
    bgColor: "bg-emerald-50"
  },
  {
    number: 4,
    title: "Deposit just $1",
    description: "Yes, just one dollar! That's all you need.",
    icon: DollarSign,
    color: "text-amber-600",
    bgColor: "bg-amber-50"
  },
  {
    number: 5,
    title: "Get $25 automatically",
    description: "Deposited within 1-2 business days",
    icon: Clock,
    color: "text-pink-600",
    bgColor: "bg-pink-50"
  },
  {
    number: 6,
    title: "You now have $26 to invest!",
    description: "Start your investing journey today 🎉",
    icon: Sparkles,
    color: "text-emerald-600",
    bgColor: "bg-emerald-50"
  }
];

const WealthsimpleStepsModal = ({ open, onOpenChange }) => {
  const handleContinue = () => {
    window.open(WEALTHSIMPLE_LINK, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="wealthsimple-steps-modal">
        <DialogHeader>
          <div className="text-center mb-2">
            <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-3">
              <Sparkles className="w-4 h-4" />
              Free $25 Offer
            </div>
          </div>
          <DialogTitle className="text-center text-2xl font-bold text-slate-800">
            Get Your Free $25 in 6 Easy Steps
          </DialogTitle>
          <p className="text-center text-slate-600 text-sm mt-1">
            Follow these simple steps to claim your free money!
          </p>
        </DialogHeader>

        {/* Steps List */}
        <div className="mt-4 space-y-3">
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors"
            >
              <div className={`flex-shrink-0 w-10 h-10 rounded-full ${step.bgColor} flex items-center justify-center`}>
                <step.icon className={`w-5 h-5 ${step.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-400">STEP {step.number}</span>
                </div>
                <p className="font-semibold text-slate-800 text-sm">{step.title}</p>
                <p className="text-xs text-slate-600 mt-0.5">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA Button */}
        <motion.div 
          className="mt-6"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Button
            onClick={handleContinue}
            className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white py-6 text-lg font-semibold rounded-xl shadow-lg shadow-emerald-200 hover:shadow-emerald-300 transition-all"
            data-testid="continue-to-wealthsimple-btn"
          >
            Continue to Wealthsimple
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          
          <p className="text-center text-xs text-slate-500 mt-3">
            You&apos;ll be redirected to Wealthsimple&apos;s secure signup page
          </p>
        </motion.div>

        {/* Trust Badges */}
        <div className="mt-4 flex items-center justify-center gap-4 text-xs text-slate-500">
          <div className="flex items-center gap-1">
            <Shield className="w-3 h-3" />
            <span>Secure</span>
          </div>
          <div className="flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" />
            <span>No Hidden Fees</span>
          </div>
          <div className="flex items-center gap-1">
            <Sparkles className="w-3 h-3" />
            <span>Real $25</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WealthsimpleStepsModal;
